/**
 * Pure MCP client config snippets for the /docs portal generator.
 * No DOM — unit-tested.
 */

export type DocsSetupConfig = {
  publicOrigin: string;
  mcpResource: string;
  prmUrl: string;
  mcpClientId: string;
  mcpScope: string;
  cognitoIssuer: string;
  /** GitLab release page for the stdio PAT bridge (B13). */
  bridgeReleaseUrl: string;
};

export const DEFAULT_BRIDGE_RELEASE_URL =
  "https://gitlab.com/mcpto/inbox/-/releases";

/** Claude Desktop / clients that accept url + optional headers. */
export function claudeDesktopOauthSnippet(cfg: DocsSetupConfig): string {
  return JSON.stringify(
    {
      mcpServers: {
        inbox: {
          url: cfg.mcpResource,
        },
      },
    },
    null,
    2,
  );
}

/** Claude Desktop with PAT Bearer (fallback when OAuth discovery is unavailable). */
export function claudeDesktopPatSnippet(
  cfg: DocsSetupConfig,
  patPlaceholder = "email_pat_REPLACE_ME",
): string {
  return JSON.stringify(
    {
      mcpServers: {
        inbox: {
          url: cfg.mcpResource,
          headers: {
            Authorization: `Bearer ${patPlaceholder}`,
          },
        },
      },
    },
    null,
    2,
  );
}

/** Claude Code remote HTTP MCP (OAuth discovery preferred). */
export function claudeCodeOauthSnippet(cfg: DocsSetupConfig): string {
  return `claude mcp add --transport http inbox ${cfg.mcpResource}`;
}

/** Claude Code / stdio clients via local PAT bridge (GitLab release asset). */
export function claudeCodeBridgeSnippet(
  cfg: DocsSetupConfig,
  patEnv = "INBOX_PAT",
): string {
  return [
    `# Download bridge from GitLab releases: ${cfg.bridgeReleaseUrl}`,
    `# export ${patEnv}=email_pat_…   # mint in SPA; never commit`,
    `# export INBOX_MCP_URL=${cfg.mcpResource}`,
    `claude mcp add --transport stdio inbox -- node /path/to/inbox-stdio-bridge.mjs`,
  ].join("\n");
}

export function docsWizardSections(cfg: DocsSetupConfig): Array<{
  id: string;
  title: string;
  body: string[];
}> {
  const clientId =
    cfg.mcpClientId ||
    "(set VITE_MCP_CLIENT_ID / COGNITO_MCP_CLIENT_ID after deploy)";
  return [
    {
      id: "oauth",
      title: "OAuth discovery (preferred)",
      body: [
        `Resource URL: ${cfg.mcpResource}`,
        `PRM (RFC 9728): ${cfg.prmUrl}`,
        `Published MCP client_id (DCR returns this same id): ${clientId}`,
        `Cognito scope to request: openid ${cfg.mcpScope}`,
        cfg.cognitoIssuer
          ? `Authorization server: ${cfg.cognitoIssuer}`
          : "Authorization server: see PRM authorization_servers[0] after deploy",
        "Flow: unauthenticated POST /mcp → 401 + WWW-Authenticate resource_metadata → fetch PRM → Cognito authorize (PKCE S256) → Bearer access token on /mcp.",
        "SPA login cookies authenticate the control plane only — they do not authenticate /mcp.",
      ],
    },
    {
      id: "pat",
      title: "PAT fallback",
      body: [
        "Mint a PAT in the app (Tokens panel). Raw token shown once; format email_pat_… (legacy inbox_pat_… still accepted during soak).",
        "Present as Authorization: Bearer email_pat_… on https://mcpto.email",
        "Scopes: read / send / full (enforced when pats.scopesEnforce is on). Prefer least privilege.",
        "Never paste PATs into chat, commits, or screenshots.",
      ],
    },
    {
      id: "attachments",
      title: "Attachments honesty",
      body: [
        "MCP get_attachment: omit mode — server auto-selects inline base64 ≤256 KB, else presigned HTTPS GET url. mode=file is rejected on hosted.",
        "Multi-account: list_accounts returns capabilities (mail/labels/folders/contacts/calendar/settings/drive/docs/…). Omit account only if one account; else pass account or search_messages.account_ids[].",
        "Google extras (Gmail only, opt-in): Contacts includes Other contacts + Workspace directory. Calendar includes list + free/busy. Drive includes Shared Drives (Team Drives) via list_shared_drives / shared_drive_id — no extra OAuth scope. Docs / Sheets / Slides are structured APIs (drive still lists/exports those files). Tasks and Meet are separate toggles. Enable on Connect Gmail / account edit + Apply with Google. Enable the matching GCP APIs (Drive, Docs, Sheets, Slides, Tasks, Meet, People, Calendar).",
        "Search: mailbox roles INBOX/SENT/…; Gmail ALL/ALL_MAIL for all mail. move_messages returns id_map (IMAP ids change).",
        "Send: prefer create_draft then send_message({draft_id}); dry_run=true previews without sending. Tool errors are JSON {error:{code,message}}.",
        "Send attachments (size-branched): ≤~1 MB → content_base64; larger → create_attachment_upload + PUT + upload_id. Soft warnings[] if oversized base64.",
        "Host path: stdio bridge rewrites attachments:[{path}] on the host. Direct remote MCP rejects path.",
      ],
    },
    {
      id: "bridge",
      title: "Stdio PAT bridge",
      body: [
        "For clients that only speak stdio MCP or cannot attach Authorization headers.",
        `Download: GitLab release asset + .sha256 (not npm) — ${cfg.bridgeReleaseUrl}`,
        "Env: INBOX_PAT, INBOX_MCP_URL (default https://mcpto.email).",
        "Run: node inbox-stdio-bridge.mjs (line-delimited JSON-RPC on stdio).",
        "Prefer native OAuth discovery or remote HTTP + headers when the client supports them.",
      ],
    },
    {
      id: "google",
      title: "Google Testing checklist",
      body: [
        "Google OAuth app remains External + Testing through Phase B — no leave-Testing, no Production-unverified, no CASA.",
        "Every Gmail user must be on the Google Cloud Console test-user list before Connect Gmail works.",
        "Refresh tokens expire about every 7 days under Testing — expect weekly Reconnect Gmail (needs_reauth status).",
        "Do not treat weekly reauth as pressure to leave Testing or start brand verification in B.",
      ],
    },
  ];
}
